import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Star, MapPin, Phone } from 'lucide-react';

const BusinessCard = ({ business }) => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  const name = language === 'ta' && business.name_ta ? business.name_ta : business.name;
  const address = language === 'ta' && business.address_ta ? business.address_ta : business.address;
  const categoryName = language === 'ta' ? business.category_name_ta : business.category_name_en;

  const handleClick = () => {
    navigate(`/business/${business.id}`);
  };

  const handleCall = (e) => {
    e.stopPropagation();
    window.location.href = `tel:${business.phone}`;
  };

  return (
    <Card
      className="group cursor-pointer overflow-hidden bg-white border border-slate-100 hover:border-blue-300 card-3d shadow-md"
      onClick={handleClick}
      data-testid={`business-card-${business.id}`}
    >
      <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-blue-50 to-slate-100">
        <img
          src={business.images?.[0] || 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=400'}
          alt={name}
          className="w-full h-full object-cover img-zoom"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {business.avg_rating > 0 && (
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-md px-2.5 py-1.5 rounded-full flex items-center gap-1 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 animate-pulse" />
            <span className="text-sm font-bold text-slate-900">{business.avg_rating}</span>
          </div>
        )}
        {categoryName && (
          <div className="absolute top-3 left-3 bg-gradient-to-r from-blue-600 to-blue-700 backdrop-blur-md px-3 py-1.5 rounded-full shadow-lg transform group-hover:translate-x-1 transition-transform duration-300">
            <span className="text-xs text-white font-semibold">{categoryName}</span>
          </div>
        )}
        
        {/* Shine effect on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
        </div>
      </div>
      
      <CardContent className="p-4 relative">
        {/* Decorative gradient bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-orange-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
        
        <h3 className="font-bold text-lg text-slate-900 mb-2 line-clamp-1 font-['Manrope'] group-hover:text-blue-600 transition-colors duration-300">
          {name}
        </h3>
        
        <div className="flex items-start gap-2 text-slate-600 text-sm mb-3 group-hover:text-slate-700 transition-colors duration-300">
          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#E85A24] group-hover:scale-110 transition-transform duration-300" />
          <span className="line-clamp-2">{address}</span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="flex items-center gap-1 text-slate-500 text-sm group-hover:text-yellow-600 transition-colors duration-300">
            <Star className="h-4 w-4 group-hover:fill-yellow-100" />
            <span className="font-medium">{business.review_count} {t('reviews')}</span>
          </div>
          
          <Button
            size="sm"
            variant="outline"
            className="flex items-center gap-1.5 btn-scale border-[#E85A24] text-[#E85A24] hover:bg-[#E85A24] hover:text-white font-semibold shadow-sm"
            onClick={handleCall}
            data-testid={`call-btn-${business.id}`}
          >
            <Phone className="h-4 w-4" />
            {t('call')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default BusinessCard;
